import { useIntl, FormattedMessage } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { linkClick } from '../../components/routerLink';

/**
 * What the Zeiterfassung says the day spanned — OFFERED, not imposed.
 *
 * The Tagebuch's working times are the author's account of the day; the booked
 * times are a second, independent record of it. So each end — and the break
 * between them — is a button that fills the field, disabled once the two
 * already agree, and never a value that writes itself.
 *
 * Span and break are offered independently: a crew can book a Pause on a day
 * whose start and end nobody completed.
 */
export default function DiaryWorkSpanHint({
    span,
    pauseMinutes,
    currentStart,
    currentEnd,
    currentPauseMinutes,
    onApplyStart,
    onApplyEnd,
    onApplyPause,
    entriesPath,
    onOpenEntries
}: {
    /** First start and last end booked on this day, "HH:mm" (null if none). */
    span: { start: string; end: string } | null;
    /** The day's most common booked Pause in minutes (null if nobody booked). */
    pauseMinutes: number | null;
    currentStart: string;
    currentEnd: string;
    currentPauseMinutes: number;
    onApplyStart: (time: string) => void;
    onApplyEnd: (time: string) => void;
    onApplyPause: (minutes: number) => void;
    /** Through to the rows the span was derived from; omitted in the field app. */
    entriesPath?: string;
    onOpenEntries?: () => void;
}) {
    const intl = useIntl();
    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1
            }}
        >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <FormattedMessage id="diary.timeHint.label" />
            </Typography>
            {span && (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'diary.timeHint.applyStart'
                    })}
                >
                    {/* A disabled button fires no events, so the tooltip needs a
                        wrapper. */}
                    <span>
                        <Button
                            size="small"
                            startIcon={<ArrowUpwardIcon />}
                            disabled={currentStart === span.start}
                            onClick={() => onApplyStart(span.start)}
                        >
                            <FormattedMessage
                                id="diary.timeHint.start"
                                values={{ time: span.start }}
                            />
                        </Button>
                    </span>
                </Tooltip>
            )}
            {span && (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'diary.timeHint.applyEnd'
                    })}
                >
                    <span>
                        <Button
                            size="small"
                            startIcon={<ArrowUpwardIcon />}
                            disabled={currentEnd === span.end}
                            onClick={() => onApplyEnd(span.end)}
                        >
                            <FormattedMessage
                                id="diary.timeHint.end"
                                values={{ time: span.end }}
                            />
                        </Button>
                    </span>
                </Tooltip>
            )}
            {pauseMinutes !== null && (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'diary.timeHint.applyBreak'
                    })}
                >
                    <span>
                        <Button
                            size="small"
                            startIcon={<ArrowUpwardIcon />}
                            disabled={currentPauseMinutes === pauseMinutes}
                            onClick={() => onApplyPause(pauseMinutes)}
                        >
                            <FormattedMessage
                                id="diary.timeHint.break"
                                values={{ minutes: pauseMinutes }}
                            />
                        </Button>
                    </span>
                </Tooltip>
            )}
            {/* A figure you cannot check is one you have to take on trust. */}
            {entriesPath && onOpenEntries && (
                <Tooltip
                    title={intl.formatMessage({
                        id: 'diary.timeHint.openEntries'
                    })}
                >
                    <IconButton
                        size="small"
                        component={RouterLink}
                        to={entriesPath}
                        aria-label={intl.formatMessage({
                            id: 'diary.timeHint.openEntries'
                        })}
                        onClick={linkClick(onOpenEntries)}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}
